import { useEffect, useMemo, useState } from "react"

import {
	useOnliePartsEcommService,
	isNonParentCustomerPart,
	NonParentCustomerPart,
	OnlinePartEcomm,
	CustomerPart,
} from "@ncs/ncs-api"
import { useChangeCallback } from "@ncs/web-legos"

import { useShopContext } from "~/contexts"

export function mapOnlinePartToCustomerPart(part: OnlinePartEcomm): CustomerPart {
	const test: CustomerPart = {
		id: String(part.id),
		description: part.description ?? null,
		flatShippingRate: null,
		imageUrl: part.imageUrl ?? null,
		childImage: part.childImage ?? null,
		isActive: Boolean(part.isActive),
		isShippedFlatRate: false,
		netPrice: part.netPrice !== undefined ? Number(part.netPrice) : null,
		childNetPrice: part.childNetPrice ?? null,
		onlinePartNumber: part.onlinePartNumber ?? null,
		part: {
			id: String(part.partId),
			description: part.description,
			partNumber: part.partNumber,
			unitOfMeasure: part.unitOfMeasure,
		},
		price: part.price ?? null,
		shippingLeadDays: 0,
		title: part.title ?? null,
		isParent: Boolean(part.isParent),
		isChild: false,
		variantCount: part.variantCount ?? 0,
		brandName: part.brandName ?? null,
		featuredItem: false,
		clearanceItem: Boolean(part.clearanceItem),
		clearanceItemDiscount: part.clearanceItemDiscount ?? null,
		variants: [],
	}
	return test
}

export const useUpdatePartTotalsEcommService = (): void => {
	const [{ cart }, shopDispatch] = useShopContext()
	const onlinePartsIds = cart.map(({ part }) => part.id)
	const onlineParts = cart.map(({ part }) => part)

	const [customerParts, setCustomerParts] = useState<CustomerPart[] | null>(null)

	// Feed the cart parts into the parts endpoint so we're getting back the very latest
	// price data, just in case we're coming back to this page after some time has gone
	// by and data could have gone stale.
	const [customerPartsData, isLoadingCustomerParts, { query }] = useOnliePartsEcommService({
		params: {
			onlinePartIds: onlinePartsIds,
		},
	})

	useEffect(() => {
		if (!isLoadingCustomerParts && customerPartsData) {
			const partsData = customerPartsData.map((part) => mapOnlinePartToCustomerPart(part))
			setCustomerParts(partsData)
		}
	}, [customerPartsData])

	// The online parts endpoint has the side effect of turning our NonParentCustomerParts into CustomerParts.
	// Flip 'em back.
	const partsData = useMemo(() => {
		const parts: NonParentCustomerPart[] = []

		if (cart.length === 0) {
			return parts
		}

		if (customerParts) {
			customerParts.forEach((onlinePart) => {
				if (!isNonParentCustomerPart(onlinePart)) {
					throw new Error(
						"There was a parent part in the cart. This should not have happened"
					)
				} else {
					parts.push(onlinePart)
				}
			})
		}
		return parts
	}, [customerParts, cart])

	// When we get back updated parts data from the server, put that into the cart.
	useChangeCallback(partsData, (updatedPartsData) => {
		// If a user had added products to their cart anonymously, and they are now logging in to checkout
		// We need to make sure that they do not have any parts that are not included in their catalogue
		const updatedPartsNumbers = updatedPartsData.map(({ part }) => part.partNumber)
		const partsToRemove = onlineParts.filter(({ part }) => {
			if (updatedPartsNumbers.length > 0) {
				return !updatedPartsNumbers.includes(part.partNumber || "")
			} else {
				return false
			}
		})

		// If they do, we take em out.
		partsToRemove.forEach((part) => {
			shopDispatch({
				type: "remove from cart",
				payload: part.id,
			})
		})

		// Put the updated parts into the cart.
		shopDispatch({
			type: "update cart parts part data",
			payload: {
				parts: updatedPartsData,
			},
		})
	})
}
